import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Download, FileJson, FileText, Copy, Check,
  X, AlertTriangle, CheckCircle, Folder, ChevronRight,
  Loader2, Package, ArrowRight, Info
} from 'lucide-react';

interface TemplateImportExportProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'import' | 'export';
  // For export mode
  selectedTemplates?: {
    id: string;
    name: string;
    category: string;
    content: string;
    variables?: any[];
    metadata?: Record<string, any>;
  }[];
  // For import mode
  onImport?: (templates: any[]) => void;
}

interface ExportFormat {
  id: 'json' | 'zip' | 'single';
  name: string;
  description: string;
  icon: any;
}

const EXPORT_FORMATS: ExportFormat[] = [
  {
    id: 'json',
    name: 'JSON Bundle',
    description: 'All templates in a single JSON file',
    icon: FileJson,
  },
  {
    id: 'zip',
    name: 'ZIP Archive',
    description: 'Individual template files in a ZIP',
    icon: Package,
  },
  {
    id: 'single',
    name: 'Single Template',
    description: 'Export one template as JSON',
    icon: FileText,
  },
];

interface ImportedTemplate {
  name: string;
  category: string;
  content: string;
  variables?: any[];
  valid: boolean;
  errors?: string[];
}

export function TemplateImportExport({ 
  isOpen, 
  onClose, 
  mode, 
  selectedTemplates = [],
  onImport 
}: TemplateImportExportProps) {
  const [exportFormat, setExportFormat] = useState<'json' | 'zip' | 'single'>('json');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeVariables, setIncludeVariables] = useState(true);
  const [exportedJson, setExportedJson] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  const [importedTemplates, setImportedTemplates] = useState<ImportedTemplate[]>([]);
  const [importing, setImporting] = useState(false);
  const [importStep, setImportStep] = useState<'upload' | 'review' | 'complete'>('upload');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle Export
  const handleExport = useCallback(() => {
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      source: 'Goettelman Document Platform',
      templates: selectedTemplates.map(t => ({
        name: t.name,
        category: t.category,
        content: t.content,
        ...(includeVariables && t.variables ? { variables: t.variables } : {}),
        ...(includeMetadata && t.metadata ? { metadata: t.metadata } : {}),
      })),
    };

    if (exportFormat === 'json' || exportFormat === 'single') {
      const jsonString = JSON.stringify(exportData, null, 2);
      setExportedJson(jsonString);
    } else if (exportFormat === 'zip') {
      // In production, this would create a ZIP file
      alert('ZIP export would download a file in production');
    }
  }, [selectedTemplates, exportFormat, includeMetadata, includeVariables]);

  // Download export
  const downloadExport = useCallback(() => {
    if (!exportedJson) return;
    
    const blob = new Blob([exportedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `templates-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [exportedJson]);

  // Copy to clipboard
  const copyToClipboard = useCallback(() => {
    if (!exportedJson) return;
    navigator.clipboard.writeText(exportedJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [exportedJson]);

  // Handle file import
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const data = JSON.parse(content);

        // Validate and parse templates
        const templates: ImportedTemplate[] = [];
        const templateArray = data.templates || [data]; // Support single template or array

        templateArray.forEach((t: any) => {
          const errors: string[] = [];
          
          if (!t.name) errors.push('Missing template name');
          if (!t.content) errors.push('Missing template content');
          if (t.content && !t.content.includes('{{')) {
            errors.push('Warning: No variables detected in template');
          }

          templates.push({
            name: t.name || 'Unnamed Template',
            category: t.category || 'Imported',
            content: t.content || '',
            variables: t.variables,
            valid: errors.length === 0 || errors.every(e => e.startsWith('Warning')),
            errors: errors.length > 0 ? errors : undefined,
          });
        });

        setImportedTemplates(templates);
        setImportStep('review');
      } catch (error) {
        alert('Invalid JSON file. Please check the file format.');
      }
    };
    reader.readAsText(file);
  }, []);

  // Complete import
  const completeImport = useCallback(() => {
    setImporting(true);
    
    // Simulate import delay
    setTimeout(() => {
      const validTemplates = importedTemplates.filter(t => t.valid);
      onImport?.(validTemplates);
      setImporting(false);
      setImportStep('complete');
    }, 1000);
  }, [importedTemplates, onImport]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-2xl max-h-[90vh] bg-primary-900 rounded-xl border border-primary-700 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
          <div className="flex items-center gap-3">
            {mode === 'export' ? (
              <Download className="w-6 h-6 text-accent-400" />
            ) : (
              <Upload className="w-6 h-6 text-accent-400" />
            )}
            <div>
              <h2 className="text-xl font-semibold text-white">
                {mode === 'export' ? 'Export Templates' : 'Import Templates'}
              </h2>
              {mode === 'export' && selectedTemplates.length > 0 && (
                <p className="text-sm text-primary-400">
                  {selectedTemplates.length} template{selectedTemplates.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-primary-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {mode === 'export' ? (
            <div className="space-y-6">
              {/* Export Format Selection */}
              {!exportedJson && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-primary-300 mb-3">
                      Export Format
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {EXPORT_FORMATS.map(format => (
                        <button
                          key={format.id}
                          onClick={() => setExportFormat(format.id)}
                          className={`p-4 rounded-lg border text-left transition-all ${
                            exportFormat === format.id
                              ? 'border-accent-500 bg-accent-500/10'
                              : 'border-primary-700 hover:border-primary-600'
                          }`}
                        >
                          <format.icon className={`w-6 h-6 mb-2 ${
                            exportFormat === format.id ? 'text-accent-400' : 'text-primary-400'
                          }`} />
                          <p className={`font-medium ${
                            exportFormat === format.id ? 'text-white' : 'text-primary-200'
                          }`}>{format.name}</p>
                          <p className="text-xs text-primary-500 mt-1">{format.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeMetadata}
                        onChange={(e) => setIncludeMetadata(e.target.checked)}
                        className="w-4 h-4 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
                      />
                      <span className="text-primary-200">Include metadata (creation date, author, etc.)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeVariables}
                        onChange={(e) => setIncludeVariables(e.target.checked)}
                        className="w-4 h-4 rounded border-primary-600 bg-primary-800 text-accent-500 focus:ring-accent-500"
                      />
                      <span className="text-primary-200">Include variable definitions</span>
                    </label>
                  </div>

                  {/* Selected Templates */}
                  <div>
                    <label className="block text-sm font-medium text-primary-300 mb-2">
                      Templates to Export
                    </label>
                    <div className="space-y-2 max-h-48 overflow-auto">
                      {selectedTemplates.map(t => (
                        <div key={t.id} className="flex items-center gap-3 p-3 bg-primary-800/50 rounded-lg">
                          <FileText className="w-5 h-5 text-accent-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{t.name}</p>
                            <p className="text-xs text-primary-400">{t.category}</p>
                          </div>
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleExport}
                    disabled={selectedTemplates.length === 0}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-5 h-5" />
                    Generate Export
                  </button>
                </>
              )}

              {/* Export Result */}
              {exportedJson && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-primary-300">Export Generated Successfully</p>
                    <div className="flex gap-2">
                      <button
                        onClick={copyToClipboard}
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary-700 text-white rounded hover:bg-primary-600"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        onClick={downloadExport}
                        className="flex items-center gap-2 px-3 py-1.5 bg-accent-500 text-white rounded hover:bg-accent-600"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-primary-950 rounded-lg p-4 max-h-80 overflow-auto">
                    <pre className="text-xs text-primary-300 font-mono whitespace-pre-wrap">
                      {exportedJson}
                    </pre>
                  </div>

                  <button
                    onClick={() => setExportedJson(null)}
                    className="text-sm text-primary-400 hover:text-white"
                  >
                    ← Back to options
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Import Mode */
            <div className="space-y-6">
              {/* Step 1: Upload */}
              {importStep === 'upload' && (
                <>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-primary-600 rounded-xl p-12 text-center cursor-pointer hover:border-accent-500 hover:bg-accent-500/5 transition-colors"
                  >
                    <Upload className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                    <p className="text-lg text-white mb-2">Drop your template file here</p>
                    <p className="text-sm text-primary-400">Supports .json files</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>

                  <div className="bg-primary-800/50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-white mb-1">Import Format</h3>
                        <p className="text-sm text-primary-400">
                          Templates should be in JSON format, either as a single template object
                          or an array of templates. Templates exported from this platform are
                          fully compatible.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Step 2: Review */}
              {importStep === 'review' && (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-white">
                      Found {importedTemplates.length} template{importedTemplates.length !== 1 ? 's' : ''}
                    </h3>
                    <span className="text-sm text-primary-400">
                      {importedTemplates.filter(t => t.valid).length} valid
                    </span>
                  </div>

                  <div className="space-y-3 max-h-64 overflow-auto">
                    {importedTemplates.map((template, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          template.valid
                            ? 'bg-green-500/10 border-green-500/30'
                            : 'bg-red-500/10 border-red-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {template.valid ? (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : (
                              <AlertTriangle className="w-5 h-5 text-red-400" />
                            )}
                            <div>
                              <p className="font-medium text-white">{template.name}</p>
                              <p className="text-sm text-primary-400">{template.category}</p>
                            </div>
                          </div>
                        </div>
                        {template.errors && (
                          <div className="mt-2 ml-8">
                            {template.errors.map((error, i) => (
                              <p key={i} className={`text-xs ${
                                error.startsWith('Warning') ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {error}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setImportStep('upload');
                        setImportedTemplates([]);
                      }}
                      className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600"
                    >
                      Choose Different File
                    </button>
                    <button
                      onClick={completeImport}
                      disabled={importedTemplates.filter(t => t.valid).length === 0 || importing}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 disabled:opacity-50"
                    >
                      {importing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <ArrowRight className="w-5 h-5" />
                          Import {importedTemplates.filter(t => t.valid).length} Templates
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Complete */}
              {importStep === 'complete' && (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Import Complete!</h3>
                  <p className="text-primary-400 mb-6">
                    {importedTemplates.filter(t => t.valid).length} templates have been imported successfully.
                  </p>
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
